export const projectionLevels = ['score', 'categories', 'items'] as const
export type ProjectionLevel = (typeof projectionLevels)[number]

export type SharedCategory = { name: string; earnedScore: number }
export type SharedProjection = {
  level: ProjectionLevel
  displayName: string
  score: number | null
  trails: Array<number | null>
  accumulation: number | null
  freshness: string
  categories?: SharedCategory[]
  successfulItems?: string[]
}

export type SharedPerson = {
  id: string
  projection: SharedProjection
  grantedLevel: ProjectionLevel
  displayLevel: ProjectionLevel
  sync: string
  since: string
  stale: boolean
}

export function capProjectionLevel(granted: ProjectionLevel, requested: ProjectionLevel, surfaceCap?: ProjectionLevel): ProjectionLevel {
  const cap = Math.min(
    projectionLevels.indexOf(granted),
    surfaceCap === undefined ? Number.POSITIVE_INFINITY : projectionLevels.indexOf(surfaceCap),
  )
  return projectionLevels[Math.min(projectionLevels.indexOf(requested), cap)] ?? 'score'
}

export function projectionAtCap(person: SharedPerson, surfaceCap?: ProjectionLevel): SharedProjection {
  const level = capProjectionLevel(person.grantedLevel, person.displayLevel, surfaceCap)
  const base: SharedProjection = {
    level,
    displayName: person.projection.displayName,
    score: person.stale ? null : person.projection.score,
    trails: person.stale ? [] : person.projection.trails,
    accumulation: person.stale ? null : person.projection.accumulation,
    freshness: person.projection.freshness,
  }
  if (!person.stale && level !== 'score') base.categories = person.projection.categories ?? []
  if (!person.stale && level === 'items') base.successfulItems = person.projection.successfulItems ?? []
  return base
}

export function takeFragmentToken(location: Pick<Location, 'hash' | 'pathname' | 'search'>, history: Pick<History, 'replaceState'>): string | null {
  const params = new URLSearchParams(location.hash.slice(1))
  const token = params.get('t')
  history.replaceState(null, '', `${location.pathname}${location.search}`)
  if (token === null || token.length < 32 || token.length > 256) return null
  return token
}
